import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import {
  Upload, FileText, CheckCircle, AlertCircle, X, ArrowLeft, ArrowRight,
  Download, Users, Loader2, Table2, ChevronDown, ChevronRight, History,
  Phone, Mail, MapPin, Clock, MessageSquare, UserCheck, SkipForward,
  GitMerge, RefreshCw, Eye, TrendingUp, AlertTriangle, Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { marketingAPI } from '../../utils/api';

// ─── Stage step labels ───────────────────────────────────────────────────────
const STAGES = [
  { id: 1, label: 'Upload File', icon: Upload },
  { id: 2, label: 'Review Duplicates', icon: Eye },
  { id: 3, label: 'Assign & Import', icon: UserCheck },
];

const PRIORITY_COLORS = {
  hot: 'bg-rose-100 text-rose-700 border-rose-200',
  warm: 'bg-amber-100 text-amber-700 border-amber-200',
  cold: 'bg-sky-100 text-sky-700 border-sky-200',
};

const STAGE_LABELS = {
  new_lead: 'New Lead',
  in_discussion: 'In Discussion',
  not_interested: 'Not Interested',
  prospective: 'Prospective',
  payment_received: 'Payment Received',
  registered: 'Registered',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Stepper ─────────────────────────────────────────────────────────────────
function Stepper({ current }) {
  return (
    <div className="flex items-center gap-0">
      {STAGES.map((s, idx) => {
        const done = current > s.id;
        const active = current === s.id;
        const Icon = s.icon;
        return (
          <React.Fragment key={s.id}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                done ? 'bg-emerald-500 text-white' :
                active ? 'bg-indigo-600 text-white' :
                'bg-slate-200 text-slate-500'
              }`}>
                {done ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-3.5 h-3.5" />}
              </div>
              <span className={`text-sm font-medium hidden sm:block ${
                active ? 'text-indigo-700' : done ? 'text-emerald-700' : 'text-slate-400'
              }`}>{s.label}</span>
            </div>
            {idx < STAGES.length - 1 && (
              <div className={`flex-1 h-0.5 mx-3 min-w-[24px] ${done ? 'bg-emerald-400' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Enquiry History Card (per prior enquiry) ────────────────────────────────
function EnquiryHistoryCard({ enquiry, index }) {
  const [open, setOpen] = useState(index === 0);
  const stageBadge = STAGE_LABELS[enquiry.stage] || enquiry.stage;
  const stageColor = enquiry.stage === 'registered'
    ? 'bg-emerald-100 text-emerald-700'
    : enquiry.stage === 'not_interested'
    ? 'bg-rose-100 text-rose-700'
    : enquiry.stage === 'payment_received'
    ? 'bg-purple-100 text-purple-700'
    : 'bg-slate-100 text-slate-600';

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        onClick={() => setOpen(!open)}
      >
        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
          {enquiry.enquiry_number || index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-700">Enquiry #{enquiry.enquiry_number || index + 1}</span>
            <Badge className={`text-xs px-1.5 py-0 h-4 border ${stageColor}`}>{stageBadge}</Badge>
            {enquiry.consultant_name && (
              <span className="text-xs text-slate-500">by {enquiry.consultant_name}</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(enquiry.created_at)}</span>
            {enquiry.country_of_interest && <span>· {enquiry.country_of_interest}</span>}
            {enquiry.calls_count_detail > 0 && (
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{enquiry.calls_count_detail} calls</span>
            )}
          </div>
        </div>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
      </button>

      {open && (
        <div className="p-3 space-y-3 bg-white">
          {/* Last note */}
          {enquiry.last_note && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> Last Note
              </p>
              <p className="text-xs text-amber-700 leading-relaxed">{enquiry.last_note}</p>
            </div>
          )}

          {/* Calls */}
          {enquiry.calls?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
                <Phone className="w-3 h-3" /> Call Log ({enquiry.calls.length})
              </p>
              <div className="space-y-1.5">
                {enquiry.calls.slice(0, 5).map((call, ci) => (
                  <div key={ci} className="flex gap-2 text-xs bg-slate-50 rounded-lg p-2">
                    <div className="flex-1">
                      <span className="font-medium text-slate-700">{call.outcome?.replace('_', ' ')}</span>
                      {call.notes && <p className="text-slate-500 mt-0.5 line-clamp-2">{call.notes}</p>}
                    </div>
                    <span className="text-slate-400 whitespace-nowrap flex-shrink-0">{formatDate(call.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {enquiry.notes?.length > 1 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> All Notes ({enquiry.notes.length})
              </p>
              <div className="space-y-1">
                {enquiry.notes.slice(-3).map((note, ni) => (
                  <div key={ni} className="text-xs bg-slate-50 rounded-lg p-2">
                    <p className="text-slate-600 leading-relaxed">{note.content}</p>
                    <p className="text-slate-400 mt-0.5">{formatDate(note.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!enquiry.last_note && !enquiry.calls?.length && !enquiry.notes?.length && (
            <p className="text-xs text-slate-400 italic text-center py-2">No calls or notes recorded for this enquiry.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Duplicate Row (expandable) ───────────────────────────────────────────────
function DuplicateRow({ row, decision, onDecisionChange, onNotesChange, consultants }) {
  const [expanded, setExpanded] = useState(false);
  const d = row.data;
  const history = row.history || [];

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      decision === 'skip' ? 'border-slate-200 opacity-60' :
      decision === 'merge' ? 'border-purple-300 bg-purple-50/30' :
      'border-amber-300 bg-amber-50/20'
    }`}>
      {/* Main row */}
      <div className="flex items-center gap-3 p-3">
        <button
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-100 border-2 border-amber-400 flex items-center justify-center">
            <span className="text-xs font-bold text-amber-700">×{row.prior_enquiry_count}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-800 text-sm">{d.full_name}</span>
              <Badge className={`text-xs px-1.5 py-0 h-4 border ${PRIORITY_COLORS[d.priority] || PRIORITY_COLORS.warm}`}>
                {d.priority}
              </Badge>
              <Badge className="text-xs px-1.5 py-0 h-4 bg-amber-100 text-amber-700 border border-amber-200">
                {row.prior_enquiry_count} prior {row.prior_enquiry_count === 1 ? 'enquiry' : 'enquiries'}
              </Badge>
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 flex-wrap">
              {d.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{d.phone}</span>}
              {d.email && <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" />{d.email}</span>}
              {d.country_of_interest && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{d.country_of_interest}</span>}
              <span className="text-amber-600 font-medium">Matched by {row.duplicate_match_type}</span>
            </div>
          </div>
          {expanded
            ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
            : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
        </button>

        {/* Action selector */}
        <div className="flex-shrink-0 flex items-center gap-2">
          <Select value={decision} onValueChange={onDecisionChange}>
            <SelectTrigger className={`w-44 h-8 text-xs ${
              decision === 'import' ? 'border-amber-300 bg-amber-50' :
              decision === 'skip' ? 'border-slate-300' :
              'border-purple-300 bg-purple-50'
            }`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="import">
                <span className="flex items-center gap-1.5 text-xs"><RefreshCw className="w-3 h-3 text-amber-500" /> Import as Duplicate</span>
              </SelectItem>
              <SelectItem value="skip">
                <span className="flex items-center gap-1.5 text-xs"><SkipForward className="w-3 h-3 text-slate-400" /> Skip This Lead</span>
              </SelectItem>
              <SelectItem value="merge">
                <span className="flex items-center gap-1.5 text-xs"><GitMerge className="w-3 h-3 text-purple-500" /> Merge with Existing</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Expanded section: full enquiry history */}
      {expanded && (
        <div className="border-t border-amber-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-indigo-500" />
            <h4 className="text-sm font-semibold text-slate-700">
              Previous Enquiry History ({history.length})
            </h4>
            {history.length > 0 && (
              <span className="text-xs text-slate-500 ml-auto">
                Last seen: {formatDate(history[history.length - 1]?.created_at)}
              </span>
            )}
          </div>
          {history.length === 0 ? (
            <p className="text-xs text-slate-400 italic text-center py-3">No detailed history available.</p>
          ) : (
            <div className="space-y-2">
              {history.map((enq, i) => (
                <EnquiryHistoryCard key={enq.id || i} enquiry={enq} index={i} />
              ))}
            </div>
          )}

          {/* Manager notes for this lead */}
          <div className="mt-3">
            <Label className="text-xs text-slate-600">Briefing Note for New Consultant (optional)</Label>
            <textarea
              className="w-full mt-1 text-xs border border-slate-200 rounded-lg p-2 resize-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 outline-none"
              rows={2}
              placeholder="Add context for the new consultant, e.g. 'Client rejected last time due to pricing. Has budget now.'"
              onChange={(e) => onNotesChange(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── New Lead Row ──────────────────────────────────────────────────────────────
function NewLeadRow({ row }) {
  const d = row.data;
  return (
    <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl">
      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-800 text-sm">{d.full_name}</span>
          <Badge className={`text-xs px-1.5 py-0 h-4 border ${PRIORITY_COLORS[d.priority] || PRIORITY_COLORS.warm}`}>
            {d.priority}
          </Badge>
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 flex-wrap">
          {d.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{d.phone}</span>}
          {d.email && <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" />{d.email}</span>}
          {d.country_of_interest && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{d.country_of_interest}</span>}
        </div>
      </div>
      <Badge className="text-xs bg-emerald-100 text-emerald-700 border border-emerald-200">New Lead</Badge>
    </div>
  );
}

// ─── Invalid Row ──────────────────────────────────────────────────────────────
function InvalidRow({ row }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl">
      <AlertCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />
      <div className="flex-1">
        <span className="text-sm text-rose-700 font-medium">Row {row.row_index}</span>
        <span className="text-xs text-rose-500 ml-2">{row.reason}</span>
      </div>
      <Badge className="text-xs bg-rose-100 text-rose-700 border border-rose-200">Skipped</Badge>
    </div>
  );
}

// ─── Summary Bar ──────────────────────────────────────────────────────────────
function SummaryBar({ newCount, dupCount, invalidCount, total }) {
  return (
    <div className="grid grid-cols-4 gap-3">
      {[
        { label: 'Total Rows', value: total, color: 'bg-slate-100 text-slate-700', icon: Table2 },
        { label: 'New Leads', value: newCount, color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
        { label: 'Returning', value: dupCount, color: 'bg-amber-100 text-amber-700', icon: RefreshCw },
        { label: 'Invalid', value: invalidCount, color: 'bg-rose-100 text-rose-700', icon: AlertTriangle },
      ].map(({ label, value, color, icon: Icon }) => (
        <div key={label} className={`rounded-xl p-3 ${color} flex items-center gap-2`}>
          <Icon className="w-5 h-5 opacity-70 flex-shrink-0" />
          <div>
            <p className="text-2xl font-bold leading-none">{value}</p>
            <p className="text-xs opacity-70 mt-0.5">{label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MarketingLeadsUpload() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [stage, setStage] = useState(1);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  // Per-row state for stage 2
  const [decisions, setDecisions] = useState({});    // rowIndex -> "import"|"skip"|"merge"
  const [managerNotes, setManagerNotes] = useState({});  // rowIndex -> string
  const [activeTab, setActiveTab] = useState('duplicates'); // "all"|"new"|"duplicates"|"invalid"

  // Stage 3 assignment
  const [consultants, setConsultants] = useState([]);
  const [defaultConsultant, setDefaultConsultant] = useState('');
  const [assignMode, setAssignMode] = useState('pool'); // 'pool' | 'consultant'
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    marketingAPI.getConsultants()
      .then(({ data }) => setConsultants(data.consultants || []))
      .catch(() => {});
  }, []);

  // ── File handling ───────────────────────────────────────────────────────────
  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f?.name?.endsWith('.csv')) setFile(f);
    else toast.error('Only CSV files are accepted');
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
    e.target.value = '';
  };

  // ── Stage 1 → 2: Scan ──────────────────────────────────────────────────────
  const handleScan = async () => {
    if (!file) return;
    setScanning(true);
    try {
      const { data } = await marketingAPI.scanLeads(file);
      setScanResult(data);
      // Pre-fill decisions: duplicates default to "import", new leads just get "import"
      const init = {};
      data.rows.forEach((row) => {
        init[row.row_index] = 'import';
      });
      setDecisions(init);
      setActiveTab(data.duplicate_count > 0 ? 'duplicates' : 'new');
      setStage(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed. Please check the file format.');
    } finally {
      setScanning(false);
    }
  };

  // ── Stage 2 → 3 ────────────────────────────────────────────────────────────
  const handleProceedToAssign = () => {
    if (!scanResult) return;
    const toImport = scanResult.rows.filter((r) => decisions[r.row_index] !== 'skip').length;
    if (toImport === 0) {
      toast.error('All leads are set to skip. Please change at least one decision.');
      return;
    }
    setStage(3);
  };

  // ── Stage 3: Execute import ─────────────────────────────────────────────────
  const handleImport = async () => {
    if (assignMode === 'consultant' && !defaultConsultant) {
      toast.error('Please select a consultant or choose to import to pool');
      return;
    }
    setImporting(true);
    try {
      const rowDecisions = scanResult.rows.map((row) => ({
        row_index: row.row_index,
        action: decisions[row.row_index] || 'import',
        merge_lead_id: row.existing_lead_id || null,
        manager_notes: managerNotes[row.row_index] || null,
      }));

      const payload = {
        batch_id: `batch_${Date.now()}`,
        file_name: file.name,
        assigned_to: assignMode === 'consultant' ? defaultConsultant : null,
        rows: scanResult.rows.map((r) => ({ row_index: r.row_index, data: r.data })),
        decisions: rowDecisions,
        default_duplicate_action: 'import',
      };

      const { data } = await marketingAPI.importReviewed(payload);
      setImportResult(data);
      setStage(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const duplicateRows = scanResult?.rows?.filter((r) => r.is_duplicate) || [];
  const newRows = scanResult?.rows?.filter((r) => !r.is_duplicate) || [];
  const invalidRows = scanResult?.invalid_rows || [];

  const skipCount = Object.values(decisions).filter((d) => d === 'skip').length;
  const importCount = Object.values(decisions).filter((d) => d !== 'skip').length;

  const handleSetAllDupDecision = (action) => {
    const updated = { ...decisions };
    duplicateRows.forEach((r) => { updated[r.row_index] = action; });
    setDecisions(updated);
  };

  const downloadTemplate = () => {
    const headers = 'SrNo,Lead ID,Name,Lead Source,Visa Country,Visa Type,Mobile No,Email ID,Lead Category,City,Description,Lead Assign Date,Call Purpose,Call Description,Call Date,Schedule Date/Time,Lead Status,User Name';
    const sample = '1,L001,Ahmed Al-Rashid,Facebook,Canada,Permanent Residency,+971501234567,ahmed@example.com,Hot Lead,Dubai,Interested in Canada PR,,Inquiry,Called and discussed requirements,,2026-04-30 10:00,Open Lead,John Smith';
    const csv = `${headers}\n${sample}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'immiways_leads_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setStage(1);
    setFile(null);
    setScanResult(null);
    setDecisions({});
    setManagerNotes({});
    setDefaultConsultant('');
    setImportResult(null);
  };

  // ─── Stage 4: Result ──────────────────────────────────────────────────────
  if (stage === 4 && importResult) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content-bare overflow-y-auto bg-slate-50">
        <div className="p-6 max-w-3xl mx-auto space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl font-bold text-slate-900">Import Complete</h1>
          </div>

          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 border-2 border-emerald-400 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-emerald-800 mb-3">Leads Successfully Imported!</h3>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-white rounded-xl p-3 text-center">
                      <p className="text-3xl font-bold text-emerald-700">{importResult.imported}</p>
                      <p className="text-xs text-emerald-600">Imported</p>
                    </div>
                    <div className="bg-white rounded-xl p-3 text-center">
                      <p className="text-3xl font-bold text-amber-600">{importResult.duplicates ?? 0}</p>
                      <p className="text-xs text-amber-500">Duplicates</p>
                    </div>
                    <div className="bg-white rounded-xl p-3 text-center">
                      <p className="text-3xl font-bold text-slate-500">{importResult.skipped ?? 0}</p>
                      <p className="text-xs text-slate-400">Skipped</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => navigate('/admin/marketing/pool')} className="bg-teal-600 hover:bg-teal-700 gap-1.5">
                      <Users className="w-3.5 h-3.5" /> View Lead Pool
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => navigate('/admin/marketing/history')} className="gap-1.5">
                      <Table2 className="w-3.5 h-3.5" /> Import History
                    </Button>
                    <Button size="sm" variant="outline" onClick={reset}>
                      Import Another File
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    );
  }

  // ─── Stage 1: File Drop ────────────────────────────────────────────────────
  const renderStage1 = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        {!file ? (
          <div
            className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-200 ${
              dragOver ? 'border-indigo-400 bg-indigo-50' : 'border-slate-300 hover:border-indigo-300 hover:bg-slate-50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => fileRef.current.click()}
          >
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileSelect} />
            <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-lg font-semibold text-slate-700">Drop your CSV file here</p>
            <p className="text-sm text-slate-500 mt-1">or click to browse files</p>
            <Badge variant="secondary" className="mt-3">CSV files only</Badge>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 bg-white border border-indigo-200 rounded-xl p-4 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800">{file.name}</p>
                <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB · Ready to scan</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setFile(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-indigo-700">
                  <p className="font-semibold">Smart Duplicate Detection</p>
                  <p className="text-xs mt-1 text-indigo-600">
                    Clicking "Scan for Duplicates" will analyse the file against existing leads
                    using phone number and email matching. You'll review duplicates before anything is imported.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Immiways CSV Format</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-slate-600 space-y-1">
            {['Name', 'Mobile No', 'Email ID', 'Lead Category', 'Visa Country', 'Visa Type', 'City', 'Description', 'User Name', 'Schedule Date/Time', 'Lead Status'].map((col) => (
              <div key={col} className="flex items-center gap-1.5 py-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                <span>{col}</span>
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full mt-3 gap-1.5 text-xs" onClick={downloadTemplate}>
              <Download className="w-3.5 h-3.5" /> Download Template
            </Button>
          </CardContent>
        </Card>

        <Button
          className="w-full bg-teal-600 hover:bg-teal-700 gap-2 h-11"
          onClick={handleScan}
          disabled={!file || scanning}
        >
          {scanning ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Scanning...</>
          ) : (
            <><Eye className="w-4 h-4" /> Scan for Duplicates</>
          )}
        </Button>
      </div>
    </div>
  );

  // ─── Stage 2: Review ───────────────────────────────────────────────────────
  const renderStage2 = () => {
    const tabs = [
      { id: 'duplicates', label: 'Returning Enquiries', count: duplicateRows.length, color: 'text-amber-600' },
      { id: 'new', label: 'New Leads', count: newRows.length, color: 'text-emerald-600' },
      { id: 'invalid', label: 'Invalid', count: invalidRows.length, color: 'text-rose-500' },
    ];

    return (
      <div className="space-y-5">
        <SummaryBar
          newCount={scanResult.new_count}
          dupCount={scanResult.duplicate_count}
          invalidCount={scanResult.invalid_count}
          total={scanResult.total}
        />

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-slate-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              <Badge className={`text-xs px-1.5 py-0 h-4 ${
                activeTab === tab.id ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'
              }`}>
                {tab.count}
              </Badge>
            </button>
          ))}

          {/* Bulk actions for duplicates tab */}
          {activeTab === 'duplicates' && duplicateRows.length > 0 && (
            <div className="ml-auto flex items-center gap-2 pb-1">
              <span className="text-xs text-slate-500">Set all:</span>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => handleSetAllDupDecision('import')}>
                <RefreshCw className="w-3 h-3 text-amber-500" /> Import All
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => handleSetAllDupDecision('skip')}>
                <SkipForward className="w-3 h-3 text-slate-400" /> Skip All
              </Button>
            </div>
          )}
        </div>

        {/* Tab content */}
        <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
          {activeTab === 'duplicates' && (
            duplicateRows.length === 0
              ? <p className="text-center text-slate-500 text-sm py-8">No duplicate leads found. All leads are new!</p>
              : duplicateRows.map((row) => (
                  <DuplicateRow
                    key={row.row_index}
                    row={row}
                    decision={decisions[row.row_index] || 'import'}
                    onDecisionChange={(v) => setDecisions((prev) => ({ ...prev, [row.row_index]: v }))}
                    onNotesChange={(v) => setManagerNotes((prev) => ({ ...prev, [row.row_index]: v }))}
                    consultants={consultants}
                  />
                ))
          )}
          {activeTab === 'new' && (
            newRows.length === 0
              ? <p className="text-center text-slate-500 text-sm py-8">No new leads in this file.</p>
              : newRows.map((row) => <NewLeadRow key={row.row_index} row={row} />)
          )}
          {activeTab === 'invalid' && (
            invalidRows.length === 0
              ? <p className="text-center text-slate-500 text-sm py-8">No invalid rows found.</p>
              : invalidRows.map((row) => <InvalidRow key={row.row_index} row={row} />)
          )}
        </div>

        {/* Action footer */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-200">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-emerald-700">{importCount}</span> leads to import
            {skipCount > 0 && <span className="text-slate-400"> · {skipCount} skipped</span>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setStage(1)} className="gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Button>
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700 gap-1" onClick={handleProceedToAssign}>
              Assign & Import <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Stage 3: Assignment ───────────────────────────────────────────────────
  const renderStage3 = () => {
    const selected = consultants.find((c) => c.id === defaultConsultant);
    const toImport = importCount;
    return (
      <div className="max-w-xl mx-auto space-y-5">
        {/* Summary */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-indigo-800 mb-2">Ready to Import</h3>
          <div className="flex gap-6 text-sm">
            <div><span className="text-2xl font-bold text-emerald-700">{newRows.length}</span><span className="text-slate-500 ml-1.5">new leads</span></div>
            <div><span className="text-2xl font-bold text-amber-600">{duplicateRows.filter((r) => decisions[r.row_index] !== 'skip').length}</span><span className="text-slate-500 ml-1.5">returning</span></div>
            <div><span className="text-2xl font-bold text-slate-400">{Object.values(decisions).filter((d) => d === 'skip').length}</span><span className="text-slate-500 ml-1.5">skipped</span></div>
          </div>
        </div>

        {/* Assignment mode toggle */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-500" /> Assignment Mode
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Pool option */}
            <button
              className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                assignMode === 'pool' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setAssignMode('pool')}
            >
              <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${assignMode === 'pool' ? 'border-indigo-500' : 'border-slate-300'}`}>
                {assignMode === 'pool' && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">Import to Lead Pool</p>
                <p className="text-xs text-slate-500 mt-0.5">Leads go to the unassigned pool. Sales manager assigns to consultants later.</p>
              </div>
            </button>

            {/* Assign now option */}
            <button
              className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                assignMode === 'consultant' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setAssignMode('consultant')}
            >
              <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${assignMode === 'consultant' ? 'border-indigo-500' : 'border-slate-300'}`}>
                {assignMode === 'consultant' && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">Assign to Consultant Now</p>
                <p className="text-xs text-slate-500 mt-0.5">All {toImport} leads will be assigned to the selected consultant immediately.</p>
              </div>
            </button>

            {/* Consultant selector — only shown when assign mode */}
            {assignMode === 'consultant' && (
              <div className="space-y-1.5 pt-1">
                <Label>Select Consultant <span className="text-rose-500">*</span></Label>
                <Select value={defaultConsultant} onValueChange={setDefaultConsultant}>
                  <SelectTrigger><SelectValue placeholder="Select a consultant..." /></SelectTrigger>
                  <SelectContent>
                    {consultants.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="font-medium">{c.full_name}</span>
                        <span className="text-xs text-slate-500 ml-2">{c.active_leads} active leads</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selected && (
                  <div className="bg-indigo-50 rounded-lg p-2.5 text-sm text-indigo-700 font-medium">
                    {selected.full_name} — {selected.active_leads || 0} active leads
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 gap-1" onClick={() => setStage(2)}>
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Button>
          <Button
            className="flex-1 bg-teal-600 hover:bg-teal-700 gap-2"
            onClick={handleImport}
            disabled={(assignMode === 'consultant' && !defaultConsultant) || importing}
          >
            {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing...</>
              : assignMode === 'pool'
              ? <><Upload className="w-4 h-4" /> Import to Pool</>
              : <><Upload className="w-4 h-4" /> Assign & Import</>
            }
          </Button>
        </div>
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content-bare overflow-y-auto bg-slate-50">
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900">Import Leads</h1>
            <p className="text-sm text-slate-500">Scan for duplicates, review history, then assign & import</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/marketing/history')} className="gap-1.5">
            <History className="w-3.5 h-3.5" /> History
          </Button>
        </div>

        {/* Stepper */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <Stepper current={stage > 3 ? 4 : stage} />
        </div>

        {/* Stage content */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          {stage === 1 && renderStage1()}
          {stage === 2 && renderStage2()}
          {stage === 3 && renderStage3()}
        </div>
      </div>
      </div>
    </div>
  );
}
